import { GuestWaitingList, WaitingListEntity } from '@seaters/constants';

import { isIOS, isMobile, isSafari } from './deviceDetection';

const googleCalendarURL = 'https://calendar.google.com/calendar/render';
const outlookWebURL = 'https://outlook.office.com/calendar/0/action/compose';
const trustedCalendarHosts = ['calendar.google.com', 'outlook.office.com'];

type AddToCalendarProps = {
  waitingList: WaitingListEntity | GuestWaitingList;
  platform?: string;
};

type NavigationOptions = {
  allowedHosts?: string[];
  allowedProtocols?: string[];
  downloadFileName?: string;
  newTab?: boolean;
};

// Only allow the fixed calendar providers and local ICS URL schemes we create.
const navigateToAllowedUrl = (
  urlString: string,
  {
    allowedHosts = [],
    allowedProtocols = ['https:'],
    downloadFileName,
    newTab = false,
  }: NavigationOptions = {}
) => {
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(urlString, window.location.origin);
  } catch {
    return;
  }

  if (!allowedProtocols.includes(parsedUrl.protocol)) {
    return;
  }

  const requiresTrustedHost =
    parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';

  if (
    requiresTrustedHost &&
    allowedHosts.length > 0 &&
    !allowedHosts.includes(parsedUrl.hostname)
  ) {
    return;
  }

  const element = document.createElement('a');
  element.setAttribute('href', parsedUrl.toString());
  element.setAttribute('target', newTab ? '_blank' : '_self');
  element.setAttribute('rel', 'noopener noreferrer');

  if (downloadFileName) {
    element.setAttribute('download', downloadFileName);
  }

  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

const toCleanDate = (date: string | Date): string =>
  new Date(date).toISOString().replace(/[-:]/g, '').replace('.000', '');

const escapeIcsText = (text: string): string => {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
};

const getTranslationValue = (
  value:
    | WaitingListEntity['description']
    | GuestWaitingList['description']
    | WaitingListEntity['venueCity']
    | GuestWaitingList['venueCity']
    | WaitingListEntity['venueName']
    | GuestWaitingList['venueName']
    | undefined
): string => {
  if (!value) return '';

  if (Array.isArray(value)) {
    return value[0]?.text || '';
  }

  return value.en || value.fr || value.nl || '';
};

const extractEventData = (
  waitingList: WaitingListEntity | GuestWaitingList
) => {
  const DEFAULT_DURATION_MS = 2.5 * 60 * 60 * 1000;

  const getEndDate = (): Date => {
    if (waitingList.eventEndDate) {
      return new Date(waitingList.eventEndDate);
    }

    const startDate = new Date(waitingList.eventStartDate);
    return new Date(startDate.getTime() + DEFAULT_DURATION_MS);
  };

  const startDate = new Date(waitingList.eventStartDate);
  const endDate = getEndDate();

  const eventName =
    waitingList.translatedEventName ||
    getTranslationValue(waitingList.eventName);
  const description =
    ('translatedDescription' in waitingList
      ? waitingList.translatedDescription
      : undefined) || getTranslationValue(waitingList.description);
  const venue = waitingList.translatedVenueName || '';
  const city = getTranslationValue(waitingList.venueCity);
  const combinedLocation = venue && city ? `${venue}, ${city}` : venue || city;

  return {
    startDate,
    endDate,
    eventName,
    description,
    combinedLocation,
    cleanStartDate: toCleanDate(startDate),
    cleanEndDate: toCleanDate(endDate),
    isoStart: startDate.toISOString(),
    isoEnd: endDate.toISOString(),
  };
};

const generateICSContent = (
  eventData: ReturnType<typeof extractEventData>
): string => {
  const {
    cleanStartDate,
    cleanEndDate,
    eventName,
    description,
    combinedLocation,
  } = eventData;
  const icsEventName = escapeIcsText(eventName);
  const icsDescription = escapeIcsText(description);
  const icsLocation = escapeIcsText(combinedLocation);

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Seaters//Seaters App//EN',
    'BEGIN:VEVENT',
    `DTSTART:${cleanStartDate}`,
    `DTEND:${cleanEndDate}`,
    `SUMMARY:${icsEventName}`,
    `DESCRIPTION:${icsDescription}`,
    `LOCATION:${icsLocation}`,
    'BEGIN:VALARM',
    'TRIGGER:-P1D',
    'ACTION:DISPLAY',
    `DESCRIPTION:${icsEventName}`,
    'END:VALARM',
    'BEGIN:VALARM',
    'TRIGGER:-PT1H',
    'ACTION:DISPLAY',
    `DESCRIPTION:${icsEventName}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

const handleGoogleCalendar = (
  eventData: ReturnType<typeof extractEventData>
) => {
  const {
    eventName,
    description,
    combinedLocation,
    cleanStartDate,
    cleanEndDate,
  } = eventData;

  const googleLink = new URL(googleCalendarURL);
  googleLink.searchParams.set('action', 'TEMPLATE');
  googleLink.searchParams.set('text', eventName);
  googleLink.searchParams.set('dates', `${cleanStartDate}/${cleanEndDate}`);
  googleLink.searchParams.set('location', combinedLocation);
  googleLink.searchParams.set('details', description);

  if (isMobile()) {
    navigateToAllowedUrl(googleLink.toString(), {
      allowedHosts: trustedCalendarHosts,
    });
  } else {
    navigateToAllowedUrl(googleLink.toString(), {
      allowedHosts: trustedCalendarHosts,
      newTab: true,
    });
  }
};

const handleOutlookCalendar = (
  eventData: ReturnType<typeof extractEventData>
) => {
  const { eventName, description, combinedLocation, isoStart, isoEnd } =
    eventData;
  const outlookWebLink = new URL(outlookWebURL);
  outlookWebLink.searchParams.set('rru', 'addevent');
  outlookWebLink.searchParams.set('startdt', isoStart);
  outlookWebLink.searchParams.set('enddt', isoEnd);
  outlookWebLink.searchParams.set('subject', eventName);
  outlookWebLink.searchParams.set('body', description);
  outlookWebLink.searchParams.set('location', combinedLocation);
  outlookWebLink.searchParams.set('reminder', '60');

  if (isMobile()) {
    navigateToAllowedUrl(outlookWebLink.toString(), {
      allowedHosts: trustedCalendarHosts,
    });
  } else {
    navigateToAllowedUrl(outlookWebLink.toString(), {
      allowedHosts: trustedCalendarHosts,
      newTab: true,
    });
  }
};

const handleAppleCalendar = (icsContent: string) => {
  if (isMobile()) {
    if (isIOS()) {
      if (isSafari()) {
        const blob = new Blob([icsContent], {
          type: 'text/calendar;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        navigateToAllowedUrl(url, {
          allowedProtocols: ['blob:'],
        });
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } else {
        const icsLink =
          'data:text/calendar;charset=utf-8,' + encodeURIComponent(icsContent);
        navigateToAllowedUrl(icsLink, {
          allowedProtocols: ['data:'],
        });
      }
    } else {
      const blob = new Blob([icsContent], {
        type: 'text/calendar;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      navigateToAllowedUrl(url, {
        allowedProtocols: ['blob:'],
        downloadFileName: 'event.ics',
      });
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    }
  } else {
    const icsLink =
      'data:text/calendar;charset=utf-8,' + encodeURIComponent(icsContent);
    navigateToAllowedUrl(icsLink, {
      allowedProtocols: ['data:'],
      downloadFileName: 'event.ics',
    });
  }
};

export const addToCalendar = ({
  waitingList,
  platform,
}: AddToCalendarProps) => {
  const eventData = extractEventData(waitingList);

  if (platform === 'google') {
    handleGoogleCalendar(eventData);
  } else if (platform === 'outlook') {
    handleOutlookCalendar(eventData);
  } else {
    const icsContent = generateICSContent(eventData);
    handleAppleCalendar(icsContent);
  }
};
